// GENERATED by helm-console/scripts/ensure-style-runtime.mjs — DO NOT EDIT.
// Verbatim copy of helm-bridge/src/client/synthwave-grid.ts. Regenerated every console
// build; edits here are overwritten. Owner correction 2026-09-07: the style
// substrate (backgrounds/canvas, reduce-effects) runs on the Bridge home too.
// synthwave-grid.ts — SYNTHWAVE '88's animated ground: a Battlezone
// perspective grid receding in Z under a hard horizon.
//
// IT OWNS NO CANVAS. PR-4's style-axis.ts owns the element, the ONE-canvas
// cap, the rAF loop and pause-on-hidden; this module registers a PAINTER
// (`(ctx, t) => void`) and is called once per frame with the substrate's
// clock. That split is why scripts/perf-budget.mjs can prove the cap and the
// pause by driving the substrate — a style that rolled its own loop would sit
// outside every one of those arms. There is no `requestAnimationFrame` and no
// `addEventListener` in this file, and the smoke asserts their absence.
//
// WHAT IT DRAWS (spec §4).
//   * A hard HORIZON at 38% of the viewport height. ABOVE it the canvas draws
//     NOTHING — no stars (those are Starship's), no clouds, no gradient
//     beyond the body ramp. The sky is empty on purpose: the grid and the
//     chrome headline sell the genre in exactly two fixed places, and
//     everything an operator reads for a living sits on unlit outlines.
//   * BELOW it, a ground plane. Verticals radiate STRAIGHT from the vanishing
//     point at (50%, 38%) — Battlezone, not highway (R14: Quarterdeck's rhumb
//     field is planar with no horizon and no depth cue; this one is
//     perspectival and strictly below its horizon).
//   * Transverse rungs on an EXPONENTIAL curve, dense near the horizon and
//     widening toward the bottom edge, advancing TOWARD the viewer on a 12 s
//     loop. Recession in Z, not translation in XY (R13): Starship moves
//     discrete achromatic points across the picture plane, Synthwave moves
//     continuous neon lines along the depth axis. NOTHING ROTATES —
//     synthwave-3's radar sweep is struck outright, because the rotating
//     radial sweep is Destroyer's exclusively (R2.1).
//   * Strokes are 1px and DUOTONE: rungs alternate `--sea` (cyan, the
//     counter-hue) and `--accent` by slot index, fading to nothing toward the
//     vanishing point so the horizon stays clean.
//
// THE 12 s PERIOD IS LEGAL. R2.3's ≥60 s floor governs ambient ROTATION
// specifically; a translating field is governed instead by opacity (≤18%,
// clamped below and declared in CSS as `--sw-rung-alpha`) and by the fact
// that every panel in this style is 100% opaque, so the grid is visible only
// in gutters and empty zones and never behind data.
//
// TWO COMPOSITES PER FRAME, INDEPENDENT OF DENSITY. The vertical fan never
// moves, so it is rendered ONCE into an offscreen buffer and blitted; only
// the ~14 transverse rungs are stroked per frame. dpr is capped at 2 and the
// layout/theme read is throttled, so a resize costs one re-render of the fan
// rather than one per frame.
//
// WHY IT NEVER RUNS UNDER A REDUCE LANE: style-axis.ts's effectiveGrade()
// degrades `canvas` to `gradient` whenever prefers-reduced-motion OR
// prefers-reduced-transparency matches, so the element is never constructed
// and this painter is never called. styles.css paints the same geometry as a
// static frame instead — the identity survives in full, at one paint.

/** The hard ceiling on a rung's stroke alpha, mirrored here so a stylesheet
 * that loses the token cannot silently raise it. `--sw-rung-alpha` is the
 * live value; this is the ceiling it is clamped to (spec §4: 12-18%). */
export const RUNG_ALPHA_CEILING = 0.18;

/** The horizon's DEFAULT, as a fraction of viewport height. The one number
 * the CSS still frame and the painter must agree on — and since the
 * `--bgl-horizon` seat (style-axis.ts GROUND_HORIZON_SEAT) it is the
 * FALLBACK, not the value: the painter reads the resolved `--sw-horizon`
 * (which the stylesheet declares as `var(--bgl-horizon, 38%)`) so a SURFACE
 * whose open ground sits high can raise the plane. MEASURED 2026-09-08 on the
 * Bridge home: the "Jump back in" band spans ~12-31% of a 950px viewport and
 * everything below 38% is the opaque releases grid — with the horizon fixed
 * at 38% the only Synthwave ground the home ever showed was a sliver under
 * the table, and the band read as a flat panel (the owner's screenshot). */
export const HORIZON = 0.38;

/** The horizon the stylesheet resolved, as a viewport fraction: `38%` is a
 * fraction of the viewport, `109px` (a surface's chrome edge, written into
 * `--bgl-horizon` by that surface) is divided by the canvas height, a bare
 * number is taken as a fraction. Anything else — an empty token, a `var()`
 * that never resolved — is the DEFAULT, never a NaN plane. Clamped to
 * [0, .9] so a bad seat cannot push the vanishing point off the canvas and
 * leave the field invisible. Exported so the smoke can drive it. */
export function parseHorizon(raw: string, h: number): number {
  const m = /^(-?\d*\.?\d+)(%|px)?$/.exec(raw.trim());
  if (!m) return HORIZON;
  const v = parseFloat(m[1] ?? '');
  if (!Number.isFinite(v)) return HORIZON;
  const frac = m[2] === 'px' ? v / Math.max(h, 1) : m[2] === '%' ? v / 100 : v;
  return Math.min(Math.max(frac, 0), 0.9);
}

/** One full advance of the rung field. Recession, not rotation — see the
 * header for why 12 s clears R2.3. */
export const RUNG_PERIOD_MS = 12_000;

/** How many transverse rungs exist at once. EVEN, so the cyan/accent
 * alternation survives the wrap: slot j keeps its hue forever. */
const RUNGS = 14;

/** Verticals in the fan. They never move, so this costs one offscreen
 * render, not one per frame. */
const FAN_LINES = 29;

/** Steepness of the exponential rung spacing. Higher = more crowding at the
 * horizon. Tuned so the nearest rung gap is ~7× the farthest. */
const DEPTH_K = 3.4;

/** dpr is capped: a 3× backing store for 1px hairlines is pure cost. */
const MAX_DPR = 2;

/** How often the painter re-reads layout and theme tokens (ms). Both are
 * getComputedStyle / clientWidth reads — cheap, but not 60 times a second. */
const REFRESH_MS = 500;

interface Env {
  at: number;
  w: number;
  h: number;
  dpr: number;
  alpha: number;
  accent: string;
  sea: string;
  /** The horizon in force, as a viewport fraction (see parseHorizon). */
  horizon: number;
  /** The pre-rendered vertical fan, blitted once per frame. */
  fan: HTMLCanvasElement | OffscreenCanvas | null;
}

let env: Env | null = null;

/** Depth 0..1 (0 = at the horizon, 1 = at the bottom edge) -> y in CSS px.
 * Exported so the gate and the smoke can check the CSS still frame against
 * the same curve the painter uses rather than against a second one. */
export function rungY(u: number, h: number, horizon: number = HORIZON): number {
  const vp = horizon * h;
  const span = h - vp;
  return vp + span * ((Math.exp(DEPTH_K * u) - 1) / (Math.exp(DEPTH_K) - 1));
}

/** `#rrggbb` + alpha -> an `rgba()` the canvas can take. A non-hex resolved
 * value is handed through unchanged (the caller then loses the fade rather
 * than painting the wrong colour). */
function withAlpha(color: string, a: number): string {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
  const h = m?.[1];
  if (!h) return color;
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  return `rgba(${parseInt(full.slice(0, 2), 16)}, ${parseInt(full.slice(2, 4), 16)}, ${parseInt(full.slice(4, 6), 16)}, ${a})`;
}

/** Render the vertical fan once. It NEVER moves — recession is carried
 * entirely by the rungs — so this is the whole reason the frame cost is
 * independent of fan density. */
function renderFan(w: number, h: number, dpr: number, accent: string, sea: string, alpha: number, horizon: number):
  HTMLCanvasElement | OffscreenCanvas | null {
  const mk = (): HTMLCanvasElement | OffscreenCanvas | null => {
    if (typeof OffscreenCanvas === 'function') return new OffscreenCanvas(Math.round(w * dpr), Math.round(h * dpr));
    if (typeof document === 'undefined') return null;
    const c = document.createElement('canvas');
    c.width = Math.round(w * dpr);
    c.height = Math.round(h * dpr);
    return c;
  };
  const buf = mk();
  if (!buf) return null;
  const g = buf.getContext('2d') as CanvasRenderingContext2D | null;
  if (!g) return null;
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  g.clearRect(0, 0, w, h);
  g.lineWidth = 1;
  const vpx = w * 0.5;
  const vpy = horizon * h;
  // The fan spans three viewport widths at the bottom edge so the outermost
  // lines still leave the frame at a shallow angle instead of converging
  // visibly inside it.
  for (let i = 0; i < FAN_LINES; i++) {
    const f = i / (FAN_LINES - 1);
    const xBottom = -w + f * (3 * w);
    // A vertical fades toward the vanishing point exactly as the rungs do, so
    // the horizon reads as one clean line rather than as a knot of strokes.
    const grad = g.createLinearGradient(vpx, vpy, xBottom, h);
    const hue = i % 2 === 0 ? sea : accent;
    grad.addColorStop(0, withAlpha(hue, 0));
    grad.addColorStop(1, withAlpha(hue, alpha));
    g.strokeStyle = grad;
    g.beginPath();
    g.moveTo(vpx, vpy);
    g.lineTo(xBottom, h);
    g.stroke();
  }
  return buf;
}

function readEnv(canvas: HTMLCanvasElement, now: number): Env {
  const cs = getComputedStyle(document.documentElement);
  const tok = (name: string, fallback: string): string => cs.getPropertyValue(name).trim() || fallback;
  const declared = parseFloat(tok('--sw-rung-alpha', String(RUNG_ALPHA_CEILING)));
  const alpha = Number.isFinite(declared) ? Math.min(Math.max(declared, 0), RUNG_ALPHA_CEILING) : RUNG_ALPHA_CEILING;
  const w = canvas.clientWidth || 1;
  const h = canvas.clientHeight || 1;
  const dpr = Math.min(typeof devicePixelRatio === 'number' ? devicePixelRatio : 1, MAX_DPR);
  const want = { w: Math.round(w * dpr), h: Math.round(h * dpr) };
  if (canvas.width !== want.w || canvas.height !== want.h) {
    canvas.width = want.w;
    canvas.height = want.h;
  }
  const accent = tok('--accent', '#FF2FA8');
  const sea = tok('--sea', '#2DE2FF');
  // The resolved horizon: `--sw-horizon` is declared `var(--bgl-horizon, 38%)`,
  // so this reads the surface's seat when one is written and 38% when not.
  const horizon = parseHorizon(tok('--sw-horizon', `${HORIZON * 100}%`), h);
  // Re-render the fan only when something it depends on actually moved.
  const same = env && env.w === w && env.h === h && env.dpr === dpr
    && env.accent === accent && env.sea === sea && env.alpha === alpha && env.horizon === horizon && env.fan;
  return {
    at: now,
    w,
    h,
    dpr,
    alpha,
    accent,
    sea,
    horizon,
    fan: same ? env!.fan : renderFan(w, h, dpr, accent, sea, alpha, horizon),
  };
}

/**
 * THE painter. Registered against the SYNTHWAVE style id; the substrate calls
 * it once per animation frame with a monotonic clock.
 *
 * Two composites: one blit of the pre-rendered fan, then RUNGS strokes.
 */
export function paintSynthwaveGrid(ctx: CanvasRenderingContext2D, t: number): void {
  const canvas = ctx.canvas as HTMLCanvasElement;
  if (!env || t - env.at > REFRESH_MS || t < env.at) env = readEnv(canvas, t);
  const { w, h, dpr, alpha, accent, sea, horizon, fan } = env;

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);

  // ---- composite 1: the fan (below the horizon, and nothing above it) ----
  if (fan) ctx.drawImage(fan as CanvasImageSource, 0, 0, w, h);

  // ---- composite 2: the rungs, advancing toward the viewer --------------
  // `phase` runs 0 -> 1 over RUNG_PERIOD_MS. Slot j sits at depth
  // ((j + phase) / RUNGS), so every slot walks from the horizon to the bottom
  // edge and re-enters at the horizon — recession, and the alternation is
  // stable because RUNGS is even.
  const phase = ((t % RUNG_PERIOD_MS) + RUNG_PERIOD_MS) % RUNG_PERIOD_MS / RUNG_PERIOD_MS;
  ctx.lineWidth = 1;
  for (let j = 0; j < RUNGS; j++) {
    const u = ((j + phase) % RUNGS) / RUNGS;
    const y = rungY(u, h, horizon);
    // Fade toward the vanishing point: a rung at the horizon is invisible and
    // reaches full (capped) alpha only as it arrives at the bottom edge.
    const a = alpha * u;
    if (a <= 0.001) continue;
    ctx.strokeStyle = withAlpha(j % 2 === 0 ? sea : accent, a);
    ctx.beginPath();
    ctx.moveTo(0, y + 0.5);
    ctx.lineTo(w, y + 0.5);
    ctx.stroke();
  }
}

/** Test seam — the gate and the smoke drive the painter across arms. */
export function resetSynthwaveGrid(): void {
  env = null;
}

/** The theme tokens the ground is drawn from, for the contrast gate's ground
 * arm: it composites a tier fill over the WORST-CASE ground, which for this
 * style means the ground plus a rung at peak alpha. Every panel in SYNTHWAVE
 * is opaque, so the answer is "the fill, unchanged" — and that is exactly the
 * claim worth having an instrument for, because the day someone makes a fill
 * translucent the arm stops being a no-op. */
export function synthwaveGridTokens(): string[] {
  return ['--sea', '--accent'];
}
